import { useAuth } from '@/components/auth/authProvider';
import { useIsOrgReadOnly } from '@/components/auth/claimsUtils';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePalmettoFinanceUser';
import { QuoteDrawer } from '@/components/Quotes/QuoteDrawer';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { intercomTarget } from '@/helpers/intercomTarget';
import { useGetDrawerMarginStyles } from '@/hooks/useGetDrawerMarginStyles';
import { useParentDivLayoutStyles } from '@/hooks/useParentDivLayoutStyles';
import usePermissions from '@/hooks/usePermissions';
import { useGetAccountQuery } from '@/services/accounts';
import { useGetContractsQuery, useSendContractV2Mutation, useVoidContractMutation } from '@/services/contract';
import { useFetchHVACAccountPricingMutation } from '@/services/pricing';
import { useCreateQuoteMutation, useGetQuotesQuery, useVoidQuoteMutation } from '@/services/quotes';
import {
  useCreateSystemDesignMutation,
  useGetCurrentDesignQuery,
  useLazyGetCurrentDesignQuery,
} from '@/services/systemDesign';
import { Alert, Box, Button, Modal, RadioGroup, Spinner, toast, useOpenClose } from '@palmetto/palmetto-components';
import { Formik, FormikErrors } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ContractStatus,
  EquipmentPricingDetails,
  HVACAccessoryItem,
  HVACAccessoryLabels,
  HVACAccessoryType,
  HVACAirFilterType,
  HVACDiscountItem,
  HVACEquipmentItem,
  HVACEquipmentSizeEfficiencyMap,
  HVACEquipmentType,
  HVACEquipmentTypeLabels,
  HVACSitePrepItem,
  HVACSystem,
  HVACSystemCategoryDefaultEquipmentTypes,
  HVACZoningItem,
  ProgramType,
  QuoteStatus,
  UserPermissions,
  ZoningType,
} from 'types';

import RequirePermissions from '../../auth/requirePermissions';
import { EnhancedFormikForm } from '../../EnhancedFormikForm';
import { MainContainer } from '../../MainContainer';
import { calculateHVACSystemDesignTotalCost } from '../../Quotes/HVACQuoteCalculator.utils';

import { SystemSection } from './SystemSection';

interface HVACSystemDesignData extends Omit<HVACSystem, 'accessories' | 'equipment' | 'conditionedArea'> {
  conditionedArea?: number | string;
  equipment: {
    items: HVACEquipmentItem[];
    totalCost?: number | string;
  };
  accessories?: {
    items: (HVACAccessoryItem & { selected?: boolean; notSelected?: boolean })[];
  };
}

interface HVACFormValues {
  systems: HVACSystemDesignData[];
}

const flattenHVACErrors = (errors: FormikErrors<HVACFormValues>, values: HVACFormValues): string[] => {
  const messages: string[] = [];
  const systems = errors.systems as any[] | undefined;
  if (!systems) return messages;

  systems.forEach((systemErrors: any, systemIndex: number) => {
    if (!systemErrors) return;
    const systemName = values.systems[systemIndex]?.name || `System ${systemIndex + 1}`;
    const prefix = systemName;

    if (typeof systemErrors.systemCategory === 'string') {
      messages.push(`${prefix}: ${systemErrors.systemCategory}`);
    }

    if (systemErrors.equipment) {
      if (typeof systemErrors.equipment.totalCost === 'string') {
        messages.push(`${prefix} Equipment: ${systemErrors.equipment.totalCost}`);
      }
      if (typeof systemErrors.equipment.items === 'string') {
        messages.push(`${prefix} Equipment: ${systemErrors.equipment.items}`);
      } else if (Array.isArray(systemErrors.equipment.items)) {
        const equipmentItems = values.systems[systemIndex]?.equipment?.items || [];
        systemErrors.equipment.items.forEach((itemErrors: any, itemIndex: number) => {
          if (!itemErrors) return;
          const item = equipmentItems[itemIndex];
          const typeLabel = item?.type ? HVACEquipmentTypeLabels[item.type] || item.type : `Item ${itemIndex + 1}`;
          Object.values(itemErrors).forEach((msg) => {
            if (typeof msg === 'string') {
              messages.push(`${prefix} Equipment — ${typeLabel}: ${msg}`);
            }
          });
        });
      }
    }

    if (systemErrors.accessories) {
      if (typeof systemErrors.accessories.error === 'string') {
        messages.push(`${prefix} Accessories: ${systemErrors.accessories.error}`);
      }
      if (Array.isArray(systemErrors.accessories.items)) {
        const accessoryItems = values.systems[systemIndex]?.accessories?.items || [];
        systemErrors.accessories.items.forEach((itemErrors: any, itemIndex: number) => {
          if (!itemErrors) return;
          const item = accessoryItems[itemIndex];
          const typeLabel = item?.type ? HVACAccessoryLabels[item.type] || item.type : `Item ${itemIndex + 1}`;
          Object.entries(itemErrors).forEach(([key, msg]) => {
            if (typeof msg === 'string' && key !== 'selected' && key !== 'notSelected') {
              messages.push(`${prefix} Accessories — ${typeLabel}: ${msg}`);
            }
          });
        });
      }
    }
  });

  return messages;
};

const sumMediaFilterQuantity = (accessoryItems: HVACAccessoryItem[] = []): number =>
  accessoryItems.reduce(
    (total, item) =>
      item.type === HVACAccessoryType.airFilter && item.airFilterType === HVACAirFilterType.mediaFilter
        ? total + (Number(item.quantity) || 0)
        : total,
    0,
  );

const formatSystemData = (isPreliminary: boolean, values: any) => {
  return values.systems.map((system: any) => {
    const selectedAccessories = isPreliminary
      ? system?.accessories?.items?.filter(
          (
            item: HVACAccessoryItem & {
              selected?: boolean;
              notSelected?: boolean;
            },
          ) => item.selected,
        )
      : system?.accessories?.items;
    const splitAccessories = selectedAccessories?.reduce(
      (
        acc: { accessories: HVACAccessoryItem[]; zoning: HVACZoningItem[] },
        item: HVACAccessoryItem & { selected?: boolean; notSelected?: boolean },
      ) => {
        if (
          [HVACAccessoryType.zoning, ZoningType.bypassDamper, ZoningType.zoneBoard, ZoningType.zoneDamper].includes(
            item.type,
          )
        ) {
          acc.zoning.push(item as HVACZoningItem);
        } else {
          acc.accessories.push(item);
        }
        return acc;
      },
      { accessories: [] as HVACAccessoryItem[], zoning: [] as HVACZoningItem[] },
    );
    return {
      systemCategory: system.systemCategory,
      ...(system.conditionedArea != null ? { conditionedArea: Number(system.conditionedArea) } : {}),
      name: system.name,
      ahriNumber: system.ahriNumber,
      equipment: {
        totalCost: system.equipment.totalCost,
        items: system.equipment.items.map((item: HVACEquipmentItem) => ({
          type: item.type,
          name: item.name,
          manufacturer: item.manufacturer,
          model: item.model,
          quantity: item.quantity,
          productId: item.productId,
          serialNumbers: item.serialNumbers,
          size: item.size,
          efficiencies: item.efficiencies,
        })),
      },
      accessories: splitAccessories?.accessories?.length
        ? {
            totalCost: system?.accessories?.totalCost,
            items: splitAccessories.accessories?.map((item: HVACAccessoryItem) => ({
              type: item.type,
              name: item.name,
              manufacturer: item.manufacturer,
              model: item.model,
              quantity: item.quantity,
              productId: item.productId,
              serialNumbers: item.serialNumbers,
              airFilterType: item.airFilterType,
            })),
          }
        : undefined,
      zoning: splitAccessories?.zoning?.length
        ? {
            totalCost: 0,
            items: splitAccessories?.zoning?.map((item: HVACZoningItem) => ({
              type: item.type,
              name: item.name,
              quantity: item.quantity,
              productId: item.productId,
              serialNumbers: item.serialNumbers,
              description: item.description,
              manufacturer: item.manufacturer,
              model: item.model,
            })),
          }
        : undefined,
      sitePrep: system?.sitePrep
        ? {
            totalCost: system.sitePrep.totalCost,
            items: system.sitePrep.items.map((item: HVACSitePrepItem) => ({
              name: item.name,
            })),
          }
        : undefined,
      discounts: system?.discounts
        ? {
            totalCost: system.discounts.totalCost,
            items: system.discounts.items.map((item: HVACDiscountItem) => ({
              name: item.name,
            })),
          }
        : undefined,
    };
  });
};

export const HVACSystemDesign = () => {
  const { claims } = useAuth();
  const targetRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { isOpen: isDrawerOpen, handleOpen: openDrawer, handleClose: closeDrawer } = useOpenClose();

  const parentStyles = useParentDivLayoutStyles(targetRef);
  const { id } = useParams<{ id: any }>();
  const [createQuote] = useCreateQuoteMutation();
  const drawerMarginStyles = useGetDrawerMarginStyles(parentStyles, isDrawerOpen);
  const isFinCoUser = isPalmettoFinanceUser(claims);

  const { data: systemDesign } = useGetCurrentDesignQuery(
    {
      id,
      programType: ProgramType.hvac,
    },
    { skip: !id },
  );
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id, { skip: !id });
  const programType = account?.programType;
  const { data: quotes, isLoading: isQuotesLoading } = useGetQuotesQuery(
    { id, programType },
    { skip: !programType || !id },
  );
  const [voidQuote, { isLoading: isVoidingQuote }] = useVoidQuoteMutation({
    fixedCacheKey: 'useVoidQuoteMutationKey',
  });
  const { data: contracts, isLoading: areContractsLoading } = useGetContractsQuery(id, { skip: !id });
  const [fetchPricing, { data: pricingData = [], isLoading: isPricingLoading, status: pricingStatus }] =
    useFetchHVACAccountPricingMutation();

  const [sendContract, { isLoading: isSendingContract }] = useSendContractV2Mutation({
    fixedCacheKey: 'useSendContractV2MutationKey',
  });

  const isLoading = isAccountLoading || isQuotesLoading || areContractsLoading;
  const [createSystemDesign, { isLoading: isCreatingSystemDesign }] = useCreateSystemDesignMutation();
  const [getCurrentSystemDesign, { data: createdSystemDesign }] = useLazyGetCurrentDesignQuery();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const {
    isOpen: isQuoteCreationModalOpen,
    handleOpen: openQuoteCreationModal,
    handleClose: closeQuoteCreationModal,
  } = useOpenClose();
  const { comfortPlanDealerConditions } = useFlags();
  const userPermissions = usePermissions();

  const {
    isOpen: isLoadCalculationModalOpen,
    handleOpen: openLoadCalculationModal,
    handleClose: closeLoadCalculationModal,
  } = useOpenClose();
  const [formValues, setFormValues] = useState<any>({});
  const allowsAction = useIsOrgReadOnly();
  const [isPreliminary, setIsPreliminary] = useState(true);
  const [hasModelNumbers, setHasModelNumbers] = useState('no');

  useEffect(() => {
    if (systemDesign) {
      setIsPreliminary(systemDesign.isPreliminary);
      setHasModelNumbers(systemDesign.isPreliminary ? 'no' : 'yes');
    }
  }, [systemDesign]);

  const activeContract = useMemo(() => {
    return contracts?.find(
      (contract: any) => ![ContractStatus.voided, ContractStatus.rejected].includes(contract.status),
    );
  }, [contracts]);

  const [voidContract, { isLoading: isVoidingActiveContract }] = useVoidContractMutation({
    fixedCacheKey: 'useVoidContractMutationKey',
  });

  const systemDesignCost = useMemo(() => {
    if (!createdSystemDesign && !systemDesign) {
      return 0;
    }
    return calculateHVACSystemDesignTotalCost(createdSystemDesign || systemDesign);
  }, [createdSystemDesign, systemDesign]);

  const activeQuote = quotes?.find((quote: any) => quote.status === QuoteStatus.active);

  const calculateTotalCost = useCallback((values: { systems: HVACSystemDesignData[] }) => {
    const totalCost = calculateHVACSystemDesignTotalCost(values as any) || 0;

    return totalCost;
  }, []);

  const toggleDrawer = () => {
    if (isDrawerOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  };

  const handleSubmit = async (
    data: {
      systems: HVACSystemDesignData[];
    },
    { setSubmitting }: any,
    showLoadCalculationModal = true,
  ) => {
    try {
      setFormValues(data);
      const totalSystemDesignCost = calculateTotalCost(data);
      if (activeQuote && totalSystemDesignCost !== activeQuote.totalSystemCost) {
        openModal();
        return;
      }
      if (showLoadCalculationModal) {
        openLoadCalculationModal();
        return;
      }
      await createSystemDesign({
        systemDesign: {
          isPreliminary,
          systems: formatSystemData(isPreliminary, data),
        },
        accountId: id,
        programType: programType as ProgramType,
      }).unwrap();
      closeLoadCalculationModal();
      toast.success('System design saved successfully');
      await getCurrentSystemDesign({
        id,
        programType: ProgramType.hvac,
      });
      await fetchPricing({
        accountId: id,
        useCurrentSystemDesign: true,
      });
      openDrawer();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error saving system design');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * @description Voids a quote (and an active contract, if any)
   * and generates a new system design.
   */
  const handleConfirmModalSubmit = async () => {
    closeModal();
    if (formValues) {
      try {
        if (activeContract) {
          await voidContract({
            accountId: String(id),
            contractId: activeContract.id,
          }).unwrap();
          toast.success('Active Contract voided successfully');
        }
        await voidQuote({ accountId: id.toString(), quoteId: activeQuote.id.toString() }).unwrap();
        toast.success('Quote voided successfully');
        await createSystemDesign({
          systemDesign: {
            isPreliminary,
            systems: formatSystemData(isPreliminary, formValues),
          },
          accountId: id,
          programType: programType as ProgramType,
        }).unwrap();
        toast.success('System design saved successfully');
        await getCurrentSystemDesign({
          id,
          programType: ProgramType.hvac,
        });
        await fetchPricing({
          accountId: id,
          useCurrentSystemDesign: true,
        });
        openDrawer();
      } catch (e: any) {
        toast.error(e?.data?.message || 'Error saving system design');
      }
    }
  };

  const isVoiding = isVoidingQuote || isVoidingActiveContract;

  const ratesAndTerms = useMemo(() => {
    if (!pricingData || !pricingData.length) {
      return { rates: [], terms: [] };
    }

    const rates = Array.from(new Set(pricingData.map((item: any) => item.escalationRate)))
      .toSorted((a: any, b: any) => a - b)
      .map((rate: any) => ({
        label: `${(rate * 100).toFixed(2)}%`,
        value: rate.toString(),
        id: rate.toString(),
      }));
    const terms = Array.from(new Set(pricingData.map((item: any) => item.term)))
      .toSorted((a: any, b: any) => a - b)
      .map((term: any) => ({
        label: `${term} years`,
        value: term.toString(),
        id: term.toString(),
      }));
    return { rates, terms };
  }, [pricingData]);

  const handleCreateQuote = async (values: any) => {
    const creationPayload = {
      quoteData: {
        productId: values?.productId,
        externalReference: account?.externalReference || undefined,
        totalFinancedAmount: systemDesignCost,
      },
      id: account.id,
      programType: ProgramType.hvac,
    } as any;
    try {
      const quote = await createQuote(creationPayload).unwrap();
      const userIsAllowedToSendContract =
        comfortPlanDealerConditions && userPermissions.includes(UserPermissions.comfortPlanDealer);
      if (userIsAllowedToSendContract && quote?.status === QuoteStatus.active) {
        openQuoteCreationModal();
        return;
      } else {
        navigate(`/accounts/${id}`);
      }
      toast.success('Quote created successfully');
    } catch (e) {
      toast.error('Error creating quote');
      console.error(e);
    }
  };

  const handleSendContract = async () => {
    const toastId = toast.loading('Sending contract');
    try {
      if (activeContract?.quoteId === activeQuote?.id) {
        await sendContract({
          accountId: String(activeContract?.accountId),
          contractId: String(activeContract?.id),
          forceSend: true,
        }).unwrap();
        navigate(`/accounts/${id}`);
        toast.success('Contract sent successfully');
      }
    } catch (e: any) {
      if (e?.data?.errors) {
        toast.error(e?.data?.errors.join(', '));
      } else {
        toast.error(e?.data?.message || 'Error sending contract');
      }
    } finally {
      toast.dismiss(toastId);
    }
  };

  const handleCloseQuoteCreationModal = () => {
    closeQuoteCreationModal();
    navigate(`/accounts/${id}`);
  };

  const initialValues = useMemo(() => {
    if (!systemDesign || !systemDesign?.systems?.length) {
      const systemsToCreate = Array.from({ length: account?.propertyDetails?.systemsBeingReplaced || 1 });
      return {
        systems: systemsToCreate.map((_, index: number) => ({
          systemCategory: '',
          name: `System ${index + 1}`,
          equipment: {
            totalCost: 0,
            items: [],
          },
          accessories: {
            totalCost: 0,
            items: isPreliminary
              ? Object.values(HVACAccessoryType)
                  .toSorted()
                  .map((type) => ({
                    quantity: type === HVACAccessoryType.airFilter ? 1 : 0,
                    type,
                    selected: type === HVACAccessoryType.airFilter ? true : undefined,
                    notSelected: type === HVACAccessoryType.airFilter ? false : undefined,
                  }))
              : [],
          },
          sitePrep: {
            totalCost: 0,
            items: [],
          },
          discounts: {
            totalCost: 0,
            items: [],
          },
        })),
      };
    }

    return {
      systems: systemDesign?.systems?.map((system: HVACSystem) => {
        const accessoriesTotalCost = (system?.zoning?.totalCost || 0) + (system?.accessories?.totalCost || 0);
        const allAccessoryItems = [...(system?.accessories?.items || []), ...(system?.zoning?.items || [])];

        return {
          ...system,
          equipment: {
            ...system.equipment,
            totalCost: (system.equipment.totalCost || 0) + accessoriesTotalCost,
            items: system.equipment.items.map((item) => {
              const sizeEfficiencyOptions = HVACEquipmentSizeEfficiencyMap[item.type];
              const isSizeValid = sizeEfficiencyOptions?.size.values?.includes(item.size?.value || '');
              const isRequiredEquipment = (
                HVACSystemCategoryDefaultEquipmentTypes[system.systemCategory].filter(
                  (equipmentType) => equipmentType !== HVACEquipmentType.heatStrip,
                ) as HVACEquipmentType[]
              ).includes(item.type);
              return {
                ...item,
                isRequiredEquipment,
                size:
                  item.size && systemDesign.isPreliminary
                    ? {
                        unit: item.size.unit || '',
                        value: item.size.value || '',
                        label: isSizeValid ? item.size.value : `${item.size.value} *`,
                      }
                    : undefined,
                efficiencies:
                  item.efficiencies && systemDesign.isPreliminary
                    ? item.efficiencies.map((efficiency) => {
                        const efficiencyData = sizeEfficiencyOptions?.efficiencies.find(
                          (eff) => eff.unit === efficiency.unit,
                        );
                        const isEfficiencyValid = efficiencyData?.values?.includes(efficiency.value || '');
                        return {
                          unit: efficiency.unit || '',
                          value: efficiency.value || '',
                          label: isEfficiencyValid ? efficiency.value : `${efficiency.value} *`,
                        };
                      })
                    : undefined,
              };
            }),
          },
          accessories: {
            totalCost: 0,
            items: isPreliminary
              ? Object.values(HVACAccessoryType)
                  .map((type) => {
                    const existingItem = allAccessoryItems.find((item) => item.type === type);
                    const isAirFilter = type === HVACAccessoryType.airFilter;
                    // For Air Filter: if it exists in saved data, use it; if not, it was explicitly set to NO
                    // Only default to YES for brand new systems (handled in the first branch above)
                    if (isAirFilter && !existingItem) {
                      return {
                        type,
                        quantity: 0,
                        selected: false,
                        notSelected: true,
                      };
                    }
                    return {
                      ...existingItem,
                      type,
                      quantity: existingItem ? existingItem.quantity : 0,
                      selected: Boolean(existingItem),
                      notSelected: Boolean(!existingItem),
                    };
                  })
                  .toSorted((a, b) => a.type.localeCompare(b.type))
              : allAccessoryItems,
          },
          zoning: {
            totalCost: 0,
            items: [],
          },
        };
      }),
    };
  }, [systemDesign, account, isPreliminary]);

  const handleValidation = (values: HVACFormValues): FormikErrors<HVACFormValues> => {
    const errors: Record<string, any> = {};

    const ensureSystemErrors = (systemIndex: number) => {
      if (!errors.systems) errors.systems = [];
      if (!errors.systems[systemIndex]) errors.systems[systemIndex] = {};
    };

    values.systems.forEach((system, systemIndex) => {
      // System-level required fields
      if (!system.systemCategory) {
        ensureSystemErrors(systemIndex);
        errors.systems[systemIndex].systemCategory = 'System category is required';
      }

      // Equipment total cost required (can be 0 but must be present)
      if (system.equipment?.totalCost == null || system.equipment.totalCost === '') {
        ensureSystemErrors(systemIndex);
        if (!errors.systems[systemIndex].equipment) errors.systems[systemIndex].equipment = {};
        errors.systems[systemIndex].equipment.totalCost = 'Equipment total cost is required';
      }

      // Equipment items required
      const equipmentItems = system.equipment?.items || [];
      if (equipmentItems.length === 0) {
        ensureSystemErrors(systemIndex);
        if (!errors.systems[systemIndex].equipment) errors.systems[systemIndex].equipment = {};
        errors.systems[systemIndex].equipment.items = 'At least one equipment item is required';
      }

      // Equipment item-level validation
      equipmentItems.forEach((item: any, itemIndex: number) => {
        const itemErrors: any = {};
        const typeLabel = item.type
          ? HVACEquipmentTypeLabels[item.type as HVACEquipmentType] || item.type
          : `Item ${itemIndex + 1}`;

        if (isPreliminary) {
          // Preliminary: size and efficiencies required based on equipment type
          const sizeEfficiency = HVACEquipmentSizeEfficiencyMap[item.type as keyof typeof HVACEquipmentType];
          if (sizeEfficiency?.size) {
            if (!item.size?.value) {
              itemErrors.size = `Size is required for ${typeLabel}`;
            }
          }
          if (sizeEfficiency?.efficiencies && sizeEfficiency.efficiencies.length > 0) {
            if (
              !item.efficiencies ||
              item.efficiencies.length === 0 ||
              item.efficiencies.every((e: any) => !e.value)
            ) {
              itemErrors.efficiencies = `Efficiency is required for ${typeLabel}`;
            }
          }
        } else {
          // Final: manufacturer and model required
          if (!item.manufacturer) {
            itemErrors.manufacturer = `Manufacturer is required for ${typeLabel}`;
          }
          if (!item.model) {
            itemErrors.model = `Model is required for ${typeLabel}`;
          }
        }

        if (Object.keys(itemErrors).length > 0) {
          ensureSystemErrors(systemIndex);
          if (!errors.systems[systemIndex].equipment) errors.systems[systemIndex].equipment = {};
          if (!Array.isArray(errors.systems[systemIndex].equipment.items)) {
            errors.systems[systemIndex].equipment.items = [];
          }
          errors.systems[systemIndex].equipment.items[itemIndex] = itemErrors;
        }
      });

      // Accessory validation
      if (isPreliminary) {
        // Mirrors the server, which sums media filter quantity across every air filter item in the
        // system. Only selected items are submitted, so only those count toward the cap.
        const mediaFilterQuantity = sumMediaFilterQuantity(
          system.accessories?.items.filter((item) => item.selected),
        );
        system.accessories?.items.forEach((item, itemIndex) => {
          const accessoryLabel = item.type
            ? HVACAccessoryLabels[item.type] || item.type
            : `Accessory ${itemIndex + 1}`;
          if (typeof item.selected !== 'boolean' && typeof item.notSelected !== 'boolean') {
            ensureSystemErrors(systemIndex);
            if (!errors.systems[systemIndex].accessories) errors.systems[systemIndex].accessories = { items: [] };
            errors.systems[systemIndex].accessories.error = 'Please make a Yes/No selection for all accessories';
          }
          if (!item.selected && !item.notSelected) {
            ensureSystemErrors(systemIndex);
            if (!errors.systems[systemIndex].accessories) errors.systems[systemIndex].accessories = { items: [] };
            errors.systems[systemIndex].accessories.items[itemIndex] = {
              selected: true,
              notSelected: true,
            };
          }
          if (item.selected) {
            const itemErrors: any = {};
            if (!item.quantity || item.quantity <= 0) {
              itemErrors.quantity = `Quantity must be greater than 0 for ${accessoryLabel}`;
            } else if (item.quantity > 20) {
              itemErrors.quantity = `Quantity must be less than 20 for ${accessoryLabel}`;
            }
            if (item.type === HVACAccessoryType.airFilter) {
              if (!item.airFilterType) {
                itemErrors.airFilterType = 'Air filter type is required for Air Filter';
              } else if (item.airFilterType === HVACAirFilterType.mediaFilter && mediaFilterQuantity > 1) {
                itemErrors.quantity = 'Media Filter quantity cannot exceed 1 per system';
              }
            }
            if (Object.keys(itemErrors).length > 0) {
              ensureSystemErrors(systemIndex);
              if (!errors.systems[systemIndex].accessories) errors.systems[systemIndex].accessories = { items: [] };
              errors.systems[systemIndex].accessories.items[itemIndex] = {
                ...errors.systems[systemIndex].accessories.items[itemIndex],
                ...itemErrors,
              };
            }
          }
        });
      } else {
        // Final: accessories need manufacturer and model. The add/remove list allows several air
        // filter items, so the media filter cap has to be summed per system like the server does.
        const mediaFilterQuantity = sumMediaFilterQuantity(system.accessories?.items);
        system.accessories?.items.forEach((item: any, itemIndex: number) => {
          const accessoryLabel = item.type
            ? HVACAccessoryLabels[item.type as HVACAccessoryType] || item.type
            : `Accessory ${itemIndex + 1}`;
          const itemErrors: any = {};
          if (!item.manufacturer) {
            itemErrors.manufacturer = `Manufacturer is required for ${accessoryLabel}`;
          }
          if (!item.model) {
            itemErrors.model = `Model is required for ${accessoryLabel}`;
          }
          if (item.type === HVACAccessoryType.airFilter) {
            if (!item.airFilterType) {
              itemErrors.airFilterType = 'Air filter type is required for Air Filter';
            } else if (item.airFilterType === HVACAirFilterType.mediaFilter && mediaFilterQuantity > 1) {
              itemErrors.quantity = 'Media Filter quantity cannot exceed 1 per system';
            }
          }
          if (Object.keys(itemErrors).length > 0) {
            ensureSystemErrors(systemIndex);
            if (!errors.systems[systemIndex].accessories) errors.systems[systemIndex].accessories = { items: [] };
            errors.systems[systemIndex].accessories.items[itemIndex] = itemErrors;
          }
        });
      }
    });

    return errors;
  };

  return (
    <>
      <MainContainer>
        {isLoading ? (
          <Box display="block" textAlign="center" padding="lg" childGap="xl">
            <Spinner size="lg" />
          </Box>
        ) : (
          <Box
            overflow="hidden"
            childGap="lg"
            ref={targetRef}
            style={{
              marginRight: drawerMarginStyles,
              position: 'relative',
              transition: 'margin-right 0.3s ease',
            }}
          >
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              enableReinitialize={true}
              validateOnChange={true}
              validateOnMount={true}
              validate={handleValidation}
            >
              {({ isSubmitting, values, errors, submitCount, setSubmitting }) => {
                const totalSystemDesignCost = calculateTotalCost(values);
                return (
                  <EnhancedFormikForm noValidate>
                    <Box gap="lg">
                      <Box
                        borderColor="separator"
                        borderWidth="0 0 xs 0"
                        padding={{ desktop: 'lg 0 xl 0', base: 'lg 3xl' }}
                        direction="row"
                        childGap="5xl"
                        fontSize="md"
                        fontWeight="medium"
                      >
                        <Box>Account Details</Box>
                        <Box childGap="sm" padding={{ base: '0 0 0 5xl' }}>
                          <Box>Home Size (sq ft): {account?.propertyDetails?.homeSize ?? 'unknown'}</Box>
                          <Box>Existing Systems: {account?.propertyDetails?.existingSystems ?? 'unknown'}</Box>
                          <Box>
                            Systems Being Installed: {account?.propertyDetails?.systemsBeingReplaced ?? 'unknown'}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      borderColor="separator"
                      borderWidth="0 0 xs 0"
                      padding={{ desktop: 'lg 0 xl 0', base: 'lg 3xl' }}
                    >
                      <RadioGroup
                        data-intercom-target={intercomTarget('system-design', 'manufacturer-model')}
                        name="hasModelNumbersForDesign"
                        value={hasModelNumbers}
                        title="Do you know the manufacturer(s) and model(s) for the equipment/accessories?"
                        onChange={(event) => {
                          setHasModelNumbers(event.target.value);
                          if (event.target.value === 'yes') {
                            setIsPreliminary(false);
                          } else {
                            setIsPreliminary(true);
                          }
                        }}
                        options={[
                          { id: '1', label: 'Yes', value: 'yes' },
                          { id: '2', label: 'No', value: 'no' },
                        ]}
                      />
                    </Box>
                    <SystemSection isPreliminary={isPreliminary} isFinCoUser={isFinCoUser} />
                    <Box alignItems="flex-end" margin="md" padding={{ base: 'lg 0 0 0' }} gap="xs">
                      <Box direction="row" gap="xs" alignItems="center">
                        <Box fontWeight="medium" fontSize="md">
                          Total Number of Systems:
                        </Box>
                        <Box fontWeight="bold" fontSize="lg">
                          {values.systems.length}
                        </Box>
                      </Box>
                      <Box direction="row" gap="xs" alignItems="center">
                        <Box fontWeight="medium" fontSize="md">
                          Total System Design Cost:
                        </Box>
                        <Box fontWeight="bold" fontSize="lg">
                          {currencyFormatter.format(totalSystemDesignCost)}
                        </Box>
                      </Box>
                    </Box>
                    {submitCount > 0 && Object.keys(errors).length > 0 && (
                      <Box padding="sm 0">
                        <Alert
                          variant="danger"
                          hasIcon
                          title="Please fix the following errors:"
                          render={() => (
                            <Box as="ul" gap="xs" fontSize="sm">
                              {flattenHVACErrors(errors as FormikErrors<HVACFormValues>, values).map((error, i) => (
                                <li key={i}>{error}</li>
                              ))}
                            </Box>
                          )}
                        />
                      </Box>
                    )}
                    <Box
                      direction={{
                        base: 'column',
                        tablet: 'row',
                      }}
                      childGap="sm"
                      gap="lg"
                      style={{ flexShrink: 0 }}
                      padding={{ base: 'lg' }}
                      background="primary"
                      wrap={true}
                    >
                      {activeQuote ? (
                        <Box alignSelf="flex-start" direction="row" gap="lg">
                          <Box gap="xs">
                            <Box fontSize="xl" fontWeight="bold">
                              {currencyFormatter.format(activeQuote.totalSystemCost)}
                            </Box>
                            <Box fontSize="md">Total Financed Amount</Box>
                          </Box>
                          <Box gap="xs">
                            <Box direction="row" alignItems="center" gap="2xs">
                              <Box fontSize="xl" fontWeight="bold">
                                {currencyFormatter.format(
                                  activeQuote.systemPricingDetails.find((d: EquipmentPricingDetails) => d.year === 1)
                                    ?.monthlyPayment || 0,
                                )}
                              </Box>
                              <Box fontSize="md">/mo</Box>
                            </Box>
                            <Box fontSize="md">Year 1 Monthly Payment</Box>
                          </Box>
                          <Box gap="xs">
                            <Box direction="row" alignItems="center" gap="2xs">
                              <Box fontSize="xl" fontWeight="bold">
                                {activeQuote.systemPricingDetails.length}
                              </Box>
                              <Box fontSize="md">
                                {activeQuote.systemPricingDetails.length > 1 ? `years` : `year`}
                              </Box>
                            </Box>
                            <Box fontSize="md">Customer Term Length</Box>
                          </Box>
                        </Box>
                      ) : null}
                      <RequirePermissions
                        permissions={[UserPermissions.admin, UserPermissions.editor]}
                        checkAllPermissions={false}
                      >
                        <Box style={{ marginLeft: 'auto' }} direction="row" gap="sm">
                          <Button
                            as="a"
                            href={`/accounts/${id ? id : ''}`}
                            variant="secondary"
                            tone="neutral"
                            size="md"
                            isDisabled={isSubmitting || isCreatingSystemDesign || isPricingLoading || isVoiding}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="md"
                            variant="primary"
                            type="submit"
                            isDisabled={isSubmitting || isCreatingSystemDesign || isPricingLoading || isVoiding}
                            isLoading={isSubmitting || isCreatingSystemDesign || isPricingLoading || isVoiding}
                          >
                            Save & Get Pricing
                          </Button>
                        </Box>
                        <Modal
                          ariaLabelledBy="loadCalculation"
                          isOpen={isLoadCalculationModalOpen}
                          onDismiss={closeLoadCalculationModal}
                        >
                          <Modal.Header
                            id="loadCalculation"
                            title="Confirm Load Calculation and Proper Size"
                            onDismiss={closeLoadCalculationModal}
                          />
                          <Modal.Body>
                            By proceeding, you confirm that a load calculation was completed and used to properly
                            size the system to the home. After installation, the load calculation must be uploaded,
                            and the installed equipment must match its requirements. Failure to meet these
                            requirements may result in delayed or denied funding from LightReach.
                          </Modal.Body>
                          <Modal.Footer>
                            <Button
                              variant="secondary"
                              tone="neutral"
                              onClick={closeLoadCalculationModal}
                              disabled={!allowsAction || isSubmitting || isCreatingSystemDesign}
                              isLoading={isSubmitting || isVoiding || isCreatingSystemDesign}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="primary"
                              type="submit"
                              onClick={handleSubmit.bind(null, values, { setSubmitting }, false)}
                              disabled={!allowsAction || isSubmitting || isCreatingSystemDesign}
                              isLoading={isSubmitting || isVoiding || isCreatingSystemDesign}
                            >
                              Confirm
                            </Button>
                          </Modal.Footer>
                        </Modal>
                      </RequirePermissions>
                    </Box>
                  </EnhancedFormikForm>
                );
              }}
            </Formik>
            {!pricingData.length && pricingStatus !== 'uninitialized' && !isPricingLoading ? (
              <Alert variant="info" message="No pricing options available for this system design" />
            ) : null}
            <QuoteDrawer
              isOpen={isDrawerOpen}
              toggleDrawer={toggleDrawer}
              ratesAndTerms={ratesAndTerms}
              handleCreateQuote={handleCreateQuote}
              isLoading={isPricingLoading || isCreatingSystemDesign || isVoiding}
              systemDesignCost={systemDesignCost}
              isPricingLoading={isPricingLoading}
              pricingData={pricingData}
              programType={ProgramType.hvac}
            />
          </Box>
        )}
      </MainContainer>

      <Modal ariaLabelledBy="costMismatch" isOpen={isModalOpen} onDismiss={closeModal}>
        <Modal.Header id="costMismatch" title="Cost Mismatch" onDismiss={closeModal} />
        <Modal.Body>
          The total system design cost does not match the active quote cost. Would you like to void the existing
          quote {activeContract ? 'and active contract' : ''} and create a new one?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmModalSubmit} disabled={!allowsAction}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal isOpen={isQuoteCreationModalOpen} onDismiss={handleCloseQuoteCreationModal}>
        <Modal.Header
          id="quoteCreationModal"
          title="Quote Created Successfully"
          onDismiss={handleCloseQuoteCreationModal}
        />
        <Modal.Body>
          <Box gap="md" direction="column">
            <Box display="block">
              You can now send the contract to the customer for signature. To send them their contract for a{' '}
              {<strong>{activeQuote?.systemPricingDetails?.length}</strong>}{' '}
              {(activeQuote?.systemPricingDetails?.length ?? 0) > 1 ? 'years' : 'year'} Comfort Plan with an Initial
              Monthly Payment of {<strong>${activeQuote?.systemPricingDetails?.[0]?.monthlyPayment}</strong>}, click
              the button below.
            </Box>
            <Box display="block">
              The customer will also be invited to create their Palmetto Account where they must complete all the
              Notice to Proceed steps. The customer must complete all steps in their account in order to achieve NTP.
              Our chat support team is standing by to assist you!
            </Box>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleSendContract} disabled={isSendingContract}>
            Send Contract
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
